import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { Client } from "colyseus.js";
import {
  NicknameSchema,
  ActiveModeSchema,
  ChallengeRankingSchema,
  ChallengeSchema,
  CreateChallengeSchema,
  RoomCreateBodySchema,
  MAX_SUPPORTED_ARTIFACT_SCHEMA,
  PROTOCOL_VERSION,
  ReplaySchema,
  RoomCodeSchema,
  type ActiveMode,
  type Challenge,
  type CharacterId,
  type ChallengeRanking,
  type ClientCommandName,
  type ClientCommands,
  type ErrorCode,
  type MatchResult,
  type Replay,
  type RoomSnapshot,
  type RoundResult,
  type SceneProjection,
  type ServerEvents,
} from "@urlcade/contracts";
import { config, endpoint, multiplayerConfigured } from "./config";

interface RoomClient {
  roomId: string;
  sessionId: string;
  reconnectionToken: string;
  connection?: { close(code?: number, reason?: string): void };
  send<T extends ClientCommandName>(name: T, payload: ClientCommands[T]): void;
  onMessage(name: string, callback: (payload: unknown) => void): void;
  onLeave(callback: (code: number) => void): void;
  onError(callback: (code: number, message?: string) => void): void;
  leave(consented?: boolean): Promise<unknown>;
}

export interface RoomHandlers {
  onSnapshot(snapshot: RoomSnapshot): void;
  onScene(scene: SceneProjection): void;
  onRoundResult(result: RoundResult): void;
  onMatchResult(result: MatchResult): void;
  onRoomError(error: { code: ErrorCode; message: string }): void;
  onReconnect(status: ServerEvents["reconnect_status"]): void;
  onPhase(status: ServerEvents["phase_changed"]): void;
  onHint(hint: ServerEvents["hint"]): void;
  onClosing(message: string): void;
  onDisconnected(message: string): void;
}

const ignoredRoomHandlers: RoomHandlers = {
  onSnapshot: () => {}, onScene: () => {}, onRoundResult: () => {}, onMatchResult: () => {},
  onRoomError: () => {}, onReconnect: () => {}, onPhase: () => {}, onHint: () => {}, onClosing: () => {}, onDisconnected: () => {},
};

function boundedRoomLeave(room: RoomClient, timeoutMs = 900): Promise<void> {
  let timer = 0;
  const timeout = new Promise<void>((resolve) => {
    timer = window.setTimeout(() => {
      try { room.connection?.close(4000, "Client left room"); } catch { /* The transport may already be closed. */ }
      resolve();
    }, timeoutMs);
  });
  const leave = Promise.resolve().then(() => {
    try { room.send("leave_room", {}); } catch { /* A disconnected room cannot receive the explicit leave intent. */ }
    return room.leave(true);
  }).then(() => undefined, () => undefined);
  return Promise.race([leave, timeout]).finally(() => window.clearTimeout(timer));
}

function abortError(): DOMException { return new DOMException("Oda işlemi iptal edildi.", "AbortError"); }

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError();
}

function raceWithAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  throwIfAborted(signal);
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then((value) => { signal.removeEventListener("abort", onAbort); resolve(value); }, (error: unknown) => { signal.removeEventListener("abort", onAbort); reject(error); });
  });
}

export interface CreateRoomOptions {
  challengeId?: string;
  challengeVersionId?: string;
  mapVersionId?: string;
  characterId?: CharacterId;
  practice?: boolean;
  listed?: boolean;
  teamSize?: 2 | 3;
  signal?: AbortSignal;
}

interface RoomReservation { roomId: string; roomCode: string; inviteToken?: string; }

export class RoomConnection {
  private room: RoomClient;
  private reconnecting = false;
  private reconnectOrigin: RoomClient | null = null;
  private reconnectOriginEpoch = 0;
  private reconnectRequested = false;
  private terminalReconnect = false;
  private matchAborted = false;
  private abortSawLobby = false;
  private reconnectEpoch = 0;
  private reconnectStartedAt: number | null = null;
  private leftByUser = false;
  private cancelRetryWait: (() => void) | null = null;
  private handlers: RoomHandlers;
  private readonly client: Client;
  private readonly accessToken: () => Promise<string | null>;
  private lastAuthToken: string;

  constructor(client: Client, room: RoomClient, handlers: RoomHandlers, accessToken: () => Promise<string | null>, initialAccessToken: string) {
    this.client = client;
    this.room = room;
    this.handlers = handlers;
    this.accessToken = accessToken;
    this.lastAuthToken = initialAccessToken;
    this.bind(room);
  }

  updateHandlers(handlers: RoomHandlers): void { if (!this.leftByUser) this.handlers = handlers; }

  get roomId(): string { return this.room.roomId; }
  get sessionId(): string { return this.room.sessionId; }

  send<T extends ClientCommandName>(name: T, payload: ClientCommands[T]): void {
    if (this.leftByUser) return;
    // SIGNED_IN also fires on focus with the unchanged session. The room has
    // already authenticated that token; duplicate events must not consume its
    // bounded refresh budget. Keep this private and scoped to this connection.
    const token = name === "refresh_auth" ? (payload as ClientCommands["refresh_auth"]).token : undefined;
    if (token !== undefined && token === this.lastAuthToken) return;
    this.room.send(name, payload);
    if (token !== undefined) this.lastAuthToken = token;
  }

  async leave(): Promise<void> {
    if (this.leftByUser) return;
    this.leftByUser = true;
    this.reconnectRequested = false;
    this.lastAuthToken = "";
    this.handlers = ignoredRoomHandlers;
    this.cancelRetryWait?.();
    this.cancelRetryWait = null;
    await boundedRoomLeave(this.room);
  }

  async retryReconnect(): Promise<void> {
    await this.tryReconnect();
  }

  private abortReconnectEpisode(): void {
    if (!this.matchAborted) { this.reconnectEpoch++; this.abortSawLobby = false; }
    this.matchAborted = true;
    this.reconnectRequested = false;
    this.cancelRetryWait?.();
  }

  private bind(room: RoomClient): void {
    const on = <K extends keyof ServerEvents>(name: K, callback: (payload: ServerEvents[K]) => void) => {
      room.onMessage(name, (payload) => {
        if (!this.leftByUser && this.room === room && isObject(payload)) callback(payload as ServerEvents[K]);
      });
    };
    on("room_snapshot", (snapshot) => {
      // The same authorized room can start a new match after an abort. Only
      // its ordered lobby -> countdown transition re-arms that episode.
      if (this.matchAborted && snapshot.phase === "lobby") this.abortSawLobby = true;
      else if (this.matchAborted && this.abortSawLobby && snapshot.phase === "countdown" && !this.terminalReconnect) {
        this.matchAborted = false; this.abortSawLobby = false;
        this.reconnectStartedAt = null; this.reconnectEpoch++;
      }
      this.handlers.onSnapshot(snapshot);
    });
    on("scene", (scene) => this.handlers.onScene(scene));
    on("room_error", (error) => {
      // No success ACK exists for refresh_auth. A rejection must allow the
      // same current credential to be retried on a later auth event.
      if (["RATE_LIMITED", "AUTH_INVALID", "AUTH_EXPIRED"].includes(error.code)) this.lastAuthToken = "";
      if (error.code === "MATCH_ABORTED") this.abortReconnectEpisode();
      if (["ACCESS_REVOKED", "AUTH_INVALID", "AUTH_EXPIRED", "VERSION_MISMATCH"].includes(error.code)) {
        this.terminalReconnect = true;
        this.reconnectRequested = false;
      }
      this.handlers.onRoomError(error);
    });
    on("phase_changed", (phase) => this.handlers.onPhase(phase));
    on("round_result", (result) => this.handlers.onRoundResult(result));
    on("match_result", (result) => this.handlers.onMatchResult(result));
    on("reconnect_status", (status) => {
      if (status.status === "aborted") this.abortReconnectEpisode();
      this.handlers.onReconnect(status);
    });
    on("hint", (hint) => this.handlers.onHint(hint));
    on("server_closing", (closing) => this.handlers.onClosing(closing.message));
    room.onError((_code, message) => {
      if (this.leftByUser || this.room !== room) return;
      if (_code === 401 || _code === 403) { this.terminalReconnect = true; this.reconnectRequested = false; }
      this.handlers.onRoomError({ code: "ROOM_CLOSED", message: message || "Oda bağlantısında sorun oluştu." });
    });
    room.onLeave((code) => {
      if (this.leftByUser || this.room !== room || (this.reconnecting && this.reconnectOrigin === room && this.reconnectOriginEpoch === this.reconnectEpoch)) return;
      this.handlers.onDisconnected(`Bağlantı kesildi (${code}). Yeniden bağlanmayı deniyoruz.`);
      if (!this.terminalReconnect && !this.matchAborted) { this.reconnectRequested = true; void this.tryReconnect(); }
    });
  }

  private async tryReconnect(): Promise<void> {
    if (this.reconnecting || this.leftByUser || this.terminalReconnect || this.matchAborted) return;
    this.reconnecting = true;
    this.reconnectOrigin = this.room;
    this.reconnectRequested = false;
    const epoch = this.reconnectEpoch;
    this.reconnectOriginEpoch = epoch;
    const token = this.room.reconnectionToken;
    // A queued replacement must not obtain a fresh retry window.
    const startedAt = this.reconnectStartedAt ?? Date.now();
    this.reconnectStartedAt = startedAt;
    let lastError: unknown;
    const waits = [0, 500, 1200, 2500, 4000];
    try {
      for (const wait of waits) {
        if (wait) await new Promise<void>((resolve) => {
          let timer = 0;
          const finish = () => {
            window.clearTimeout(timer);
            if (this.cancelRetryWait === finish) this.cancelRetryWait = null;
            resolve();
          };
          timer = window.setTimeout(finish, wait);
          this.cancelRetryWait = finish;
        });
        if (Date.now() - startedAt > 13_000 || this.leftByUser || this.terminalReconnect || this.matchAborted || epoch !== this.reconnectEpoch) break;
        try {
          // Reconnect resumes the seat's original schema capability without upgrading it.
          const room = await this.client.reconnect(token) as unknown as RoomClient;
          if (epoch !== this.reconnectEpoch) { if (this.room !== room) await boundedRoomLeave(room); return; }
          if (this.leftByUser || this.terminalReconnect) { await boundedRoomLeave(room); return; }
          this.room = room;
          this.bind(room);
          const refreshedToken = await this.accessToken();
          if (epoch !== this.reconnectEpoch) return;
          if (this.leftByUser || this.terminalReconnect) { await boundedRoomLeave(room); return; }
          // A replacement socket may close while the session getter awaits.
          // Its own reconnection token must be retried after this latch releases.
          if (this.reconnectRequested) return;
          if (refreshedToken) this.send("refresh_auth", { token: refreshedToken });
          this.reconnectStartedAt = null;
          this.handlers.onReconnect({ status: "resumed", remainingMs: 0 });
          return;
        } catch (error) {
          lastError = error;
          if (epoch !== this.reconnectEpoch) return;
          const value = error as { code?: unknown; status?: unknown; message?: unknown } | null;
          if (value?.message === "MATCH_ABORTED") { this.abortReconnectEpisode(); this.handlers.onReconnect({ status: "aborted", remainingMs: 0 }); return; }
          if (value && ([401, 403, 404].includes(Number(value.code ?? value.status)) ||
            ["AUTH_INVALID", "AUTH_EXPIRED", "ACCESS_REVOKED", "FORBIDDEN", "ROOM_NOT_FOUND", "VERSION_MISMATCH"].includes(String(value.message)))) {
            this.terminalReconnect = true;
            this.reconnectRequested = false;
            break;
          }
          if (this.reconnectRequested) return;
        }
      }
    } finally {
      this.reconnecting = false;
      this.reconnectOrigin = null;
      if (this.reconnectRequested && !this.leftByUser && !this.terminalReconnect && !this.matchAborted) void this.tryReconnect();
    }
    if (epoch !== this.reconnectEpoch || this.matchAborted) return;
    if (!this.leftByUser) {
      this.terminalReconnect = true;
      this.reconnectRequested = false;
      this.handlers.onReconnect({ status: "aborted", remainingMs: 0 });
      this.handlers.onDisconnected(lastError instanceof Error ? lastError.message : "Odaya yeniden bağlanılamadı.");
    }
  }
}

export class MultiplayerService {
  private readonly supabase: SupabaseClient | null;
  private readonly client: Client | null;
  private connection: RoomConnection | null = null;
  private sessionBootstrap: Promise<Session> | null = null;
  private sessionEpoch = 0;

  constructor() {
    this.supabase = multiplayerConfigured
      ? createClient(config.supabaseUrl, config.supabasePublishableKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      })
      : null;
    this.client = multiplayerConfigured ? new Client(config.gameServerUrl) : null;
    this.supabase?.auth.onAuthStateChange((_event, session) => {
      const token = session?.access_token;
      if (token && this.connection) this.connection.send("refresh_auth", { token });
    });
  }

  get enabled(): boolean { return Boolean(this.supabase && this.client); }
  get currentConnection(): RoomConnection | null { return this.connection; }

  async getBearerToken(): Promise<string> {
    return (await this.ensureSession()).access_token;
  }

  async requestAccountEmail(emailInput: string): Promise<void> {
    if (!this.supabase) throw new Error("AUTH_UNAVAILABLE");
    const before = await this.ensureSession();
    const email = emailInput.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) throw new Error("ACCOUNT_EMAIL_INVALID");
    const updated = await this.supabase.auth.updateUser({ email }, {
      emailRedirectTo: new URL("/profile", window.location.origin).toString(),
    });
    if (updated.error || !updated.data.user || updated.data.user.id !== before.user.id) throw new Error("ACCOUNT_UPGRADE_FAILED");
  }

  async setAccountPassword(passwordInput: string): Promise<void> {
    if (!this.supabase) throw new Error("AUTH_UNAVAILABLE");
    const before = await this.ensureSession();
    const password = passwordInput;
    if (password.length < 10 || password.length > 128) throw new Error("ACCOUNT_PASSWORD_INVALID");
    if (!before.user.email || !before.user.email_confirmed_at) throw new Error("ACCOUNT_EMAIL_NOT_CONFIRMED");
    const updated = await this.supabase.auth.updateUser({ password });
    if (updated.error || !updated.data.user || updated.data.user.id !== before.user.id) throw new Error("ACCOUNT_UPGRADE_FAILED");
  }

  async createRoom(mode: ActiveMode, artifactHash: string, nicknameInput: string, handlers: RoomHandlers, options: CreateRoomOptions = {}): Promise<{ code: string; inviteToken: string; connection: RoomConnection }> {
    const nickname = validateNickname(nicknameInput);
    const { challengeId, challengeVersionId, mapVersionId, characterId, practice, listed, teamSize, signal } = options;
    const session = await raceWithAbort(this.ensureSession(), signal);
    const payload = RoomCreateBodySchema.parse({
      maxArtifactSchema: MAX_SUPPORTED_ARTIFACT_SCHEMA, mode, nickname, protocolVersion: PROTOCOL_VERSION,
      ...(mapVersionId ? { mapVersionId: validateUuid(mapVersionId) } : { artifactHash }),
      ...(characterId ? { characterId } : {}),
      ...(challengeId ? { challengeId: validateChallengeId(challengeId) } : {}),
      ...(challengeVersionId ? { challengeVersionId: validateUuid(challengeVersionId) } : {}),
      ...(practice ? { practice: true } : {}),
      ...(listed ? { listed: true } : {}),
      ...(teamSize ? { teamSize } : {}),
    });
    const reservation = await postJson<RoomReservation>("/api/rooms", payload, session.access_token, signal);
    if (!reservation.inviteToken) throw new Error("Sunucu oda davetini oluşturamadı.");
    const code = validateRoomCode(reservation.roomCode);
    const connection = await this.connect(reservation.roomId, code, nickname, session.access_token, handlers, { mode, artifactHash, mapVersionId, characterId }, signal);
    return { code, inviteToken: reservation.inviteToken, connection };
  }

  async joinRoom(codeInput: string, nicknameInput: string, handlers: RoomHandlers, signal?: AbortSignal, characterId?: CharacterId): Promise<{ code: string; inviteToken: string; connection: RoomConnection }> {
    const nickname = validateNickname(nicknameInput);
    const code = validateRoomCode(codeInput);
    const session = await raceWithAbort(this.ensureSession(), signal);
    const reservation = await postJson<RoomReservation>("/api/rooms/join", {
      maxArtifactSchema: MAX_SUPPORTED_ARTIFACT_SCHEMA, roomCode: code, nickname, protocolVersion: PROTOCOL_VERSION, ...(characterId ? { characterId } : {}),
    }, session.access_token, signal);
    const acceptedCode = validateRoomCode(reservation.roomCode);
    const connection = await this.connect(reservation.roomId, acceptedCode, nickname, session.access_token, handlers, { roomCode: acceptedCode, characterId }, signal);
    return { code: acceptedCode, inviteToken: "", connection };
  }

  async joinInvite(inviteToken: string, nicknameInput: string, handlers: RoomHandlers, signal?: AbortSignal, characterId?: CharacterId): Promise<{ code: string; inviteToken: string; connection: RoomConnection }> {
    const nickname = validateNickname(nicknameInput);
    if (!/^[A-Za-z0-9_-]{32,256}$/.test(inviteToken)) throw new Error("INVITE_INVALID");
    const session = await raceWithAbort(this.ensureSession(), signal);
    const reservation = await postJson<RoomReservation>("/api/rooms/invite", {
      maxArtifactSchema: MAX_SUPPORTED_ARTIFACT_SCHEMA, inviteToken, nickname, protocolVersion: PROTOCOL_VERSION, ...(characterId ? { characterId } : {}),
    }, session.access_token, signal);
    const code = validateRoomCode(reservation.roomCode);
    const connection = await this.connect(reservation.roomId, code, nickname, session.access_token, handlers, { roomCode: code, characterId }, signal);
    return { code, inviteToken, connection };
  }

  async getResults(): Promise<MatchResult[]> {
    const session = await this.ensureSession();
    const response = await fetch(endpoint("/api/results"), { headers: { Authorization: `Bearer ${session.access_token}`, Accept: "application/json" }, signal: AbortSignal.timeout(10_000) });
    if (!response.ok) throw new Error(await responseMessage(response, "Maç geçmişi açılamadı."));
    const body: unknown = await response.json();
    const rows = Array.isArray(body) ? body : isObject(body) && Array.isArray(body.results) ? body.results : null;
    if (!rows) throw new Error("Maç geçmişi biçimi okunamadı.");
    return rows.filter(isMatchResult);
  }

  async createChallenge(input: { artifactHash: string; optionId: string; title: string }): Promise<Challenge> {
    const request = CreateChallengeSchema.parse(input);
    const session = await this.ensureSession();
    const response = await fetch(endpoint("/api/rush-challenges"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}`, Accept: "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(12_000),
    });
    if (!response.ok) throw new Error(await responseMessage(response, "Meydan okuma sunucuda kurulamadı."));
    const parsed = ChallengeSchema.safeParse(await response.json());
    if (!parsed.success || parsed.data.artifactHash !== request.artifactHash || parsed.data.optionId !== request.optionId) {
      throw new Error("Sunucu geçerli bir meydan okuma yanıtı vermedi.");
    }
    return parsed.data;
  }

  async getChallenge(id: string): Promise<Challenge> {
    const challengeId = validateChallengeId(id);
    const response = await fetch(endpoint(`/api/rush-challenges/${challengeId}`), {
      headers: { Accept: "application/json" }, signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok) throw new Error(await responseMessage(response, "Meydan okuma açılamadı."));
    const parsed = ChallengeSchema.safeParse(await response.json());
    if (!parsed.success || parsed.data.id !== challengeId) throw new Error("Sunucu geçerli bir meydan okuma vermedi.");
    return parsed.data;
  }

  async getChallengeRankings(id: string): Promise<ChallengeRanking[]> {
    const challengeId = validateChallengeId(id);
    const response = await fetch(endpoint(`/api/rush-challenges/${challengeId}/rankings`), {
      headers: { Accept: "application/json" }, signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok) throw new Error(await responseMessage(response, "Meydan okuma sıralaması açılamadı."));
    const parsed = ChallengeRankingSchema.array().max(20).safeParse(await response.json());
    if (!parsed.success) throw new Error("Sunucu geçerli bir meydan okuma sıralaması vermedi.");
    return parsed.data;
  }

  async getReplay(id: string): Promise<Replay> {
    const replayId = validateChallengeId(id);
    const response = await fetch(endpoint(`/api/replays/${replayId}`), {
      headers: { Accept: "application/json" }, signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok) throw new Error(await responseMessage(response, "Sunucu hayalet koşuyu bulamadı."));
    const parsed = ReplaySchema.safeParse(await response.json());
    if (!parsed.success || parsed.data.id !== replayId) throw new Error("Sunucu geçerli bir hayalet koşu yanıtı vermedi.");
    return parsed.data;
  }

  async dispose(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    await connection?.leave();
  }

  async clearDeletedAccountSession(): Promise<void> {
    this.sessionEpoch++;
    this.sessionBootstrap = null;
    await this.dispose();
    if (!this.supabase) return;
    const { error } = await this.supabase.auth.signOut({ scope: "local" });
    if (error) throw new Error("LOCAL_SIGNOUT_FAILED");
  }

  private ensureSession(): Promise<Session> {
    if (!this.supabase) throw new Error("Çevrimiçi oyun yapılandırılmamış.");
    if (this.sessionBootstrap) return this.sessionBootstrap;
    const epoch = this.sessionEpoch;
    const bootstrap = (async () => {
      const existing = await this.supabase!.auth.getSession();
      if (existing.error) throw new Error("Anonim oturum okunamadı.");
      if (epoch !== this.sessionEpoch) throw new Error("AUTH_SESSION_CHANGED");
      if (existing.data.session) return existing.data.session;
      const created = await this.supabase!.auth.signInAnonymously();
      if (created.error || !created.data.session) throw new Error("Anonim oturum açılamadı. Sunucu ayarlarını kontrol et.");
      if (epoch !== this.sessionEpoch) {
        await this.supabase!.auth.signOut({ scope: "local" });
        throw new Error("AUTH_SESSION_CHANGED");
      }
      return created.data.session;
    })();
    this.sessionBootstrap = bootstrap;
    void bootstrap.finally(() => {
      if (this.sessionBootstrap === bootstrap) this.sessionBootstrap = null;
    }).catch(() => {});
    return bootstrap;
  }

  private async getAccessToken(): Promise<string | null> {
    if (!this.supabase) return null;
    const current = await this.supabase.auth.getSession();
    if (current.error || !current.data.session) return null;
    const expiresAt = current.data.session.expires_at ?? 0;
    if (expiresAt > 0 && expiresAt <= Math.floor(Date.now() / 1000) + 30) {
      const refreshed = await this.supabase.auth.refreshSession();
      if (refreshed.error || !refreshed.data.session) return null;
      return refreshed.data.session.access_token;
    }
    return current.data.session.access_token;
  }

  private async connect(roomId: string, roomCode: string, nickname: string, token: string, handlers: RoomHandlers, options: { mode?: ActiveMode; artifactHash?: string; mapVersionId?: string; roomCode?: string; characterId?: CharacterId }, signal?: AbortSignal): Promise<RoomConnection> {
    if (!this.client) throw new Error("Oyun sunucusu yapılandırılmamış.");
    await this.connection?.leave();
    throwIfAborted(signal);
    const joining = this.client.joinById(roomId, {
      token,
      nickname,
      maxArtifactSchema: MAX_SUPPORTED_ARTIFACT_SCHEMA,
      protocolVersion: PROTOCOL_VERSION,
      roomCode: options.roomCode ?? roomCode,
      ...(options.characterId ? { characterId: options.characterId } : {}),
      ...(options.mode ? { mode: options.mode } : {}),
      ...(options.artifactHash ? { artifactHash: options.artifactHash } : {}),
    }) as unknown as Promise<RoomClient>;
    let room: RoomClient;
    try { room = await raceWithAbort(joining, signal); }
    catch (error) {
      if (signal?.aborted) void joining.then((lateRoom) => boundedRoomLeave(lateRoom), () => undefined);
      throw error;
    }
    if (signal?.aborted) { await boundedRoomLeave(room); throw abortError(); }
    const connection = new RoomConnection(this.client, room, handlers, () => this.getAccessToken(), token);
    this.connection = connection;
    connection.send("request_snapshot", {});
    return connection;
  }
}

async function postJson<T>(path: string, payload: unknown, token: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(endpoint(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, Accept: "application/json" },
    body: JSON.stringify(payload),
    signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(12_000)]) : AbortSignal.timeout(12_000),
  });
  if (!response.ok) {
    if (await isRateLimitedResponse(response)) throw new Error("RATE_LIMITED");
    throw new Error(await responseMessage(response, "Oda işlemi tamamlanamadı."));
  }
  const body: unknown = await response.json();
  if (!isObject(body) || typeof body.roomId !== "string" || typeof body.roomCode !== "string" ||
      (body.inviteToken !== undefined && typeof body.inviteToken !== "string")) throw new Error("Sunucu geçerli bir oda yanıtı vermedi.");
  return body as T;
}

async function isRateLimitedResponse(response: Response): Promise<boolean> {
  if (response.status !== 429) return false;
  try {
    const body: unknown = await response.clone().json();
    return isObject(body) && body.code === "RATE_LIMITED";
  } catch {
    return false;
  }
}

function validateUuid(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) throw new Error("INVALID_ID");
  return value;
}

async function responseMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (isObject(body) && typeof body.message === "string" && body.message.length < 240) return body.message;
    if (isObject(body) && isObject(body.error) && typeof body.error.message === "string" && body.error.message.length < 240) return body.error.message;
  } catch { /* Keep the user-facing fallback if the server sends a non-JSON error. */ }
  return fallback;
}

function validateNickname(value: string): string {
  const parsed = NicknameSchema.safeParse(value);
  if (!parsed.success) throw new Error("Takma ad 2–24 karakter olmalı; harf, sayı, boşluk, nokta, tire ve alt çizgi kullan.");
  return parsed.data;
}

function validateRoomCode(value: string): string {
  const normalized = value.trim().toUpperCase();
  const parsed = RoomCodeSchema.safeParse(normalized);
  if (!parsed.success) throw new Error("Oda kodu 8 harf veya sayıdan oluşmalı.");
  return parsed.data;
}

function validateChallengeId(value: string): string {
  const parsed = ChallengeSchema.shape.id.safeParse(value);
  if (!parsed.success) throw new Error("Meydan okuma bağlantısı geçersiz.");
  return parsed.data;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isMatchResult(value: unknown): value is MatchResult {
  if (!isObject(value)) return false;
  return typeof value.matchId === "string" && ActiveModeSchema.safeParse(value.mode).success &&
    Array.isArray(value.players) && typeof value.artifactHash === "string" &&
    ["pending", "saved", "failed", "unconfigured"].includes(String(value.persistence));
}
